/**
 * Tekton Management Tab
 * Tekton 管理 - Results API + 集群 Tekton 配置概览
 */

import { useEffect, useState, useCallback } from 'react';
import {
  InfoCard,
  StructuredMetadataTable,
} from '@backstage/core-components';
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import RefreshIcon from '@material-ui/icons/Refresh';
import StorageIcon from '@material-ui/icons/Storage';
import CloudDoneIcon from '@material-ui/icons/CloudDone';
import SettingsEthernetIcon from '@material-ui/icons/SettingsEthernet';
import { useApi, discoveryApiRef, identityApiRef } from '@backstage/core-plugin-api';

const useStyles = makeStyles(theme => ({
  statCard: {
    padding: theme.spacing(2.5),
    textAlign: 'center',
    borderRadius: 12,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  statNumber: {
    fontSize: '2rem',
    fontWeight: 700,
    lineHeight: 1,
  },
  statusBanner: {
    padding: theme.spacing(1.5, 2),
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  success: {
    background: 'rgba(76, 175, 80, 0.08)',
    border: '1px solid rgba(76, 175, 80, 0.3)',
  },
  error: {
    background: 'rgba(244, 67, 54, 0.08)',
    border: '1px solid rgba(244, 67, 54, 0.3)',
  },
  warning: {
    background: 'rgba(255, 152, 0, 0.08)',
    border: '1px solid rgba(255, 152, 0, 0.3)',
  },
  resourceChip: {
    margin: 2,
    fontSize: '0.7rem',
    fontFamily: 'monospace',
  },
}));

interface TektonClusterInfo {
  clusterName: string;
  clusterTitle: string;
  hasTektonResources: boolean;
  tektonResourceTypes: string[];
}

interface TektonCheckResult {
  resultsConfigured: boolean;
  resultsUrl?: string;
  resultsReachable?: boolean;
  resultsError?: string;
  clusters: TektonClusterInfo[];
  totalClusters: number;
  tektonClusters: number;
  checkedAt: string;
}

export const TektonManagementTab: React.FC = () => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);

  const [result, setResult] = useState<TektonCheckResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const check = useCallback(async (force = false) => {
    setLoading(true);
    setError(null);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
      const { token } = await identityApi.getCredentials();
      const resp = await fetch(
        `${baseUrl}/external-services/tekton/check${force ? '?refresh=true' : ''}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setResult(await resp.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [discoveryApi, identityApi]);

  useEffect(() => { check(); }, [check]);

  if (loading && !result) {
    return (
      <Box display="flex" justifyContent="center" p={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" style={{ marginBottom: 16 }}>{error}</Alert>}

      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          size="small"
          variant="outlined"
          startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
          onClick={() => check(true)}
          disabled={loading}
          style={{ textTransform: 'none' }}
        >
          {loading ? '检测中...' : '重新检测'}
        </Button>
      </Box>

      {result && (
        <>
          {/* 统计卡片 */}
          <Grid container spacing={2} style={{ marginBottom: 24 }}>
            <Grid item xs={12} sm={4}>
              <Paper className={classes.statCard} variant="outlined">
                <StorageIcon style={{ fontSize: 28, color: result.resultsReachable ? '#4caf50' : result.resultsConfigured ? '#f44336' : '#999' }} />
                <Typography variant="caption" color="textSecondary">Results API</Typography>
                <Typography style={{
                  fontWeight: 600,
                  color: result.resultsReachable ? '#4caf50' : result.resultsConfigured ? '#f44336' : '#999',
                }}>
                  {result.resultsReachable ? '正常' : result.resultsConfigured ? '异常' : '未配置'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper className={classes.statCard} variant="outlined">
                <CloudDoneIcon style={{ fontSize: 28, color: '#1976d2' }} />
                <Typography variant="caption" color="textSecondary">总集群数</Typography>
                <Typography className={classes.statNumber} style={{ color: '#1976d2' }}>
                  {result.totalClusters}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper className={classes.statCard} variant="outlined">
                <SettingsEthernetIcon style={{ fontSize: 28, color: '#7b1fa2' }} />
                <Typography variant="caption" color="textSecondary">Tekton 集群</Typography>
                <Typography className={classes.statNumber} style={{ color: '#7b1fa2' }}>
                  {result.tektonClusters}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Tekton Results API */}
          <InfoCard title="Tekton Results API">
            {result.resultsConfigured ? (
              <>
                <Box className={`${classes.statusBanner} ${result.resultsReachable ? classes.success : classes.error}`}>
                  {result.resultsReachable ? (
                    <CheckCircleIcon style={{ color: '#4caf50' }} />
                  ) : (
                    <ErrorIcon style={{ color: '#f44336' }} />
                  )}
                  <Box>
                    <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                      {result.resultsReachable ? 'Tekton Results 服务可达' : result.resultsError || '服务不可达'}
                    </Typography>
                  </Box>
                </Box>
                <StructuredMetadataTable metadata={{
                  'API URL': result.resultsUrl || '-',
                  '认证方式': '无 (dangerously-allow-unauthenticated)',
                }} />
              </>
            ) : (
              <Alert severity="info">
                未配置 Tekton Results API 代理 (proxy./tekton-results)
              </Alert>
            )}
          </InfoCard>

          {/* 集群 Tekton 配置 */}
          <Box mt={2}>
            <InfoCard title={`集群 Tekton 资源配置 (${result.tektonClusters}/${result.totalClusters} 集群)`}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>集群</TableCell>
                      <TableCell>Tekton</TableCell>
                      <TableCell>资源类型</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.clusters.map(cluster => (
                      <TableRow key={cluster.clusterName}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" style={{ fontWeight: 500 }}>
                              {cluster.clusterTitle}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {cluster.clusterName}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {cluster.hasTektonResources ? (
                            <CheckCircleIcon style={{ fontSize: 18, color: '#4caf50' }} />
                          ) : (
                            <Typography variant="caption" color="textSecondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {cluster.hasTektonResources ? (
                            <Box display="flex" flexWrap="wrap">
                              {[...new Set(cluster.tektonResourceTypes)].map(rt => (
                                <Chip
                                  key={rt}
                                  label={rt}
                                  size="small"
                                  className={classes.resourceChip}
                                  color="primary"
                                  variant="outlined"
                                />
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="caption" color="textSecondary">无 Tekton 资源</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </InfoCard>
          </Box>

          <Box mt={2} display="flex" justifyContent="flex-end">
            <Typography variant="caption" color="textSecondary">
              检测时间: {new Date(result.checkedAt).toLocaleString('zh-CN')}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};
